import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as jobsApi from '@/api/jobs.api';
import * as contactsApi from '@/api/contacts.api';
import * as employeesApi from '@/api/employees.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useToast } from '@/hooks/useToast';
import { getContactDisplayName } from '@/lib/contact-display';
import { formatPhoneNumber } from '@/lib/format';
import { validateManualClient } from '@/lib/manual-client';
import { cn } from '@/lib/cn';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

/**
 * Modal COMPARTIDO de crear trabajo (Fase 1 trabajo manual).
 *
 * Dos entradas al mismo formulario:
 *  - "Agendar" en la ficha del lead: `contactId` (+ `conversationId`) vienen
 *    fijos — el flujo histórico, intacto.
 *  - "+ Nuevo trabajo" en /jobs: SIN `contactId` — aparece el selector de
 *    cliente (buscar existente o dar de alta uno nuevo con nombre + teléfono,
 *    la venta hecha por fuera).
 */
export interface JobFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Cliente fijo (flujo "Agendar" del lead). Sin él: selector de cliente. */
  contactId?: string;
  conversationId?: string;
}

type ClienteMode = 'existente' | 'nuevo';

export function JobFormModal({ open, onClose, contactId, conversationId }: JobFormModalProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const fixedContact = Boolean(contactId);

  // ---- Selector de cliente (solo sin contacto fijo) ----
  const [clienteMode, setClienteMode] = useState<ClienteMode>('existente');
  const [busqueda, setBusqueda] = useState('');
  const [selectedContactId, setSelectedContactId] = useState('');
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoTelefono, setNuevoTelefono] = useState('');
  const busquedaDebounced = useDebouncedValue(busqueda, 300);

  const { data: encontrados, isFetching: buscando } = useQuery({
    queryKey: ['contacts', 'picker', busquedaDebounced],
    queryFn: () => contactsApi.listContacts({ search: busquedaDebounced, take: 20 }),
    enabled: open && !fixedContact && clienteMode === 'existente' && busquedaDebounced.length >= 2,
  });

  // ---- Datos del trabajo (idénticos al Agendar histórico) ----
  const [descripcion, setDescripcion] = useState('');
  const [direccion, setDireccion] = useState('');
  const [zona, setZona] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [mapsUrl, setMapsUrl] = useState('');
  const [esGarantia, setEsGarantia] = useState(false);
  const [targetLat, setTargetLat] = useState('');
  const [targetLng, setTargetLng] = useState('');
  const [radio, setRadio] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: tecnicos } = useQuery({
    queryKey: ['employees', 'tecnicos'],
    queryFn: () => employeesApi.listEmployees({ rol: 'TECNICO', activo: true, take: 100 }),
    enabled: open,
  });

  function close() {
    setClienteMode('existente');
    setBusqueda('');
    setSelectedContactId('');
    setNuevoNombre('');
    setNuevoTelefono('');
    setDescripcion('');
    setDireccion('');
    setZona('');
    setScheduledAt('');
    setEmployeeId('');
    setMapsUrl('');
    setEsGarantia(false);
    setTargetLat('');
    setTargetLng('');
    setRadio('');
    setError(null);
    onClose();
  }

  const create = useMutation({
    mutationFn: async () => {
      // 1) Resolver el cliente: fijo (lead) / elegido / alta manual nueva.
      let jobContactId = contactId ?? selectedContactId;
      if (!fixedContact && clienteMode === 'nuevo') {
        const { contact, created } = await contactsApi.createManualContact({
          nombre: nuevoNombre,
          telefono: nuevoTelefono,
        });
        jobContactId = contact.id;
        void qc.invalidateQueries({ queryKey: ['contacts'] });
        if (!created) toast.info('Ese teléfono ya existía: se usó el cliente existente');
      }
      // 2) Crear el trabajo (mismo payload del Agendar histórico).
      const lat = parseFloat(targetLat);
      const lng = parseFloat(targetLng);
      const r = parseInt(radio, 10);
      const hasGeofence = Number.isFinite(lat) && Number.isFinite(lng) && Number.isFinite(r) && r > 0;
      return jobsApi.createJob({
        contactId: jobContactId,
        conversationId: conversationId ?? null,
        employeeId: employeeId || null,
        descripcion: descripcion.trim(),
        direccion: direccion.trim() || null,
        zona: zona.trim() || null,
        // datetime-local da hora local sin zona; toISOString la normaliza a UTC.
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        esGarantia,
        targetLat: hasGeofence ? lat : null,
        targetLng: hasGeofence ? lng : null,
        geofenceRadiusM: hasGeofence ? r : null,
        // Con coordenadas en el link (y sin geocerca manual), el backend ubica
        // el pin del mapa; el link crudo entra a la ficha de despacho.
        mapsUrl: mapsUrl.trim() || null,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success(fixedContact ? 'Trabajo agendado' : 'Trabajo creado');
      close();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'No se pudo crear el trabajo'),
  });

  function submit() {
    if (!fixedContact) {
      if (clienteMode === 'existente' && !selectedContactId) {
        setError('Elige el cliente del trabajo (o crea uno nuevo)');
        return;
      }
      if (clienteMode === 'nuevo') {
        const v = validateManualClient({ nombre: nuevoNombre, telefono: nuevoTelefono });
        if (!v.ok) {
          setError(v.error);
          return;
        }
      }
    }
    if (!descripcion.trim()) {
      setError('La descripción es obligatoria');
      return;
    }
    setError(null);
    create.mutate();
  }

  const tecnicoOptions = [
    { value: '', label: 'Sin asignar' },
    ...(tecnicos?.items ?? []).map((t) => ({ value: t.id, label: t.nombre })),
  ];

  return (
    <Modal
      open={open}
      onClose={close}
      title={fixedContact ? 'Agendar trabajo' : 'Nuevo trabajo'}
      description={
        fixedContact
          ? 'Crea un trabajo para este cliente y asígnale un técnico.'
          : 'Registra una venta hecha por fuera: cliente + trabajo, sin pasar por el agente.'
      }
      footer={
        <>
          <Button variant="ghost" type="button" onClick={close}>
            Cancelar
          </Button>
          <Button onClick={submit} loading={create.isPending}>
            {fixedContact ? 'Agendar' : 'Crear trabajo'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!fixedContact && (
          <section className="space-y-2 rounded-md border border-surface-border p-3 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Cliente</p>
              <div className="flex gap-1">
                {(
                  [
                    { value: 'existente', label: 'Existente' },
                    { value: 'nuevo', label: 'Nuevo' },
                  ] as { value: ClienteMode; label: string }[]
                ).map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setClienteMode(m.value)}
                    className={cn(
                      'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                      clienteMode === m.value
                        ? 'bg-brand-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300',
                    )}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            {clienteMode === 'existente' ? (
              <>
                <Input
                  label="Buscar cliente"
                  placeholder="Nombre o teléfono…"
                  value={busqueda}
                  onChange={(e) => {
                    setBusqueda(e.target.value);
                    setSelectedContactId('');
                  }}
                />
                {busquedaDebounced.length >= 2 && (
                  <div className="max-h-40 space-y-1 overflow-y-auto">
                    {buscando ? (
                      <p className="p-1 text-xs text-slate-400">Buscando…</p>
                    ) : (encontrados?.items ?? []).length === 0 ? (
                      <p className="p-1 text-xs text-slate-400">
                        Sin resultados — puedes crearlo con la pestaña «Nuevo».
                      </p>
                    ) : (
                      (encontrados?.items ?? []).map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setSelectedContactId(c.id)}
                          className={cn(
                            'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                            selectedContactId === c.id
                              ? 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                              : 'hover:bg-slate-50 dark:hover:bg-slate-800',
                          )}
                        >
                          <span className="truncate">{getContactDisplayName(c)}</span>
                          <span className="ml-2 shrink-0 text-xs text-slate-400">
                            {c.phone ? formatPhoneNumber(c.phone) : 'sin número'}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Input
                  label="Nombre"
                  placeholder="Nombre del cliente"
                  value={nuevoNombre}
                  onChange={(e) => setNuevoNombre(e.target.value)}
                />
                <Input
                  label="Teléfono"
                  placeholder="18095550000"
                  value={nuevoTelefono}
                  onChange={(e) => setNuevoTelefono(e.target.value)}
                />
              </div>
            )}
          </section>
        )}
        <Textarea
          label="Descripción del trabajo"
          placeholder="Describe el trabajo a realizar…"
          rows={3}
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
        />
        <Input
          label="Dirección"
          placeholder="Calle, número, sector…"
          value={direccion}
          onChange={(e) => setDireccion(e.target.value)}
        />
        <Input label="Zona" placeholder="Santo Domingo, Santiago…" value={zona} onChange={(e) => setZona(e.target.value)} />
        <Input
          label="Fecha y hora de la cita"
          type="datetime-local"
          value={scheduledAt}
          onChange={(e) => setScheduledAt(e.target.value)}
        />
        <Input
          label="Link de ubicación (Google Maps)"
          placeholder="https://maps.google.com/?q=18.48,-69.93"
          value={mapsUrl}
          onChange={(e) => setMapsUrl(e.target.value)}
        />
        <Select
          label="Técnico asignado"
          options={tecnicoOptions}
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
        />
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            checked={esGarantia}
            onChange={(e) => setEsGarantia(e.target.checked)}
          />
          Es garantía (no genera comisión)
        </label>
        <details className="rounded-md border border-surface-border p-2 dark:border-slate-700">
          <summary className="cursor-pointer text-sm text-slate-600 dark:text-slate-300">
            Geocerca / objetivo (opcional)
          </summary>
          <p className="mt-1 text-xs text-slate-400">
            Punto de destino para el chequeo de zona. Manual por ahora (geocoding de la
            dirección pendiente). Sin estos datos, solo se muestra la ubicación capturada.
          </p>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <Input label="Lat" placeholder="18.4861" value={targetLat} onChange={(e) => setTargetLat(e.target.value)} />
            <Input label="Lng" placeholder="-69.9312" value={targetLng} onChange={(e) => setTargetLng(e.target.value)} />
            <Input label="Radio (m)" placeholder="150" value={radio} onChange={(e) => setRadio(e.target.value)} />
          </div>
        </details>
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
